import { inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { IAuthState } from "ui-common/services/auth/types";
import type {
  CreateCreatorMessageResult,
  UpdateCreatorMessageResult,
  GetCreatorMessageByIdResult,
  GetMessageSegmentsResult,
} from "api-contracts/rpc/creator-message";
import type { SendCreatorMessageResult } from "api-contracts/rpc/send-creator-message";
import type {
  SendTestCreatorMessageParams,
  SendTestCreatorMessageResult,
} from "api-contracts/rpc/send-test-creator-message";
import { TEST_SEND_HOURLY_LIMIT, TEST_SEND_MAX_RECIPIENTS } from "api-contracts/rpc/send-test-creator-message";
import { NotificationService } from "../../../../../libs/ui-components/notification";
import type {
  IWritableCreatorMessageFormState,
  ICreatorMessageFormService,
  CreatorMessageFormData,
  CreatorMessageFormErrors,
  ValidateFormOptions,
} from "./types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class CreatorMessageFormService implements ICreatorMessageFormService {
  @inject<IWritableCreatorMessageFormState>("writableCreatorMessageFormState")
  private state!: IWritableCreatorMessageFormState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  private translateFn: (key: string) => string = (key) => key;
  private pendingLoad: Promise<void> | undefined;
  private pendingLoadId: string | undefined;

  setTranslationFunction(fn: (key: string) => string): void {
    this.translateFn = fn;
  }

  updateFormData<K extends keyof CreatorMessageFormData>(field: K, value: CreatorMessageFormData[K]): void {
    const current = this.state.formData.get();
    this.state.formData.set({ ...current, [field]: value });

    const errors = this.state.validationErrors.get();
    if (errors[field as keyof CreatorMessageFormErrors]) {
      const newErrors = { ...errors };
      delete newErrors[field as keyof CreatorMessageFormErrors];
      this.state.validationErrors.set(newErrors);
    }
  }

  validateForm(opts?: ValidateFormOptions): boolean {
    const data = this.state.formData.get();
    const errors: CreatorMessageFormErrors = {};

    if (!data.title.trim()) {
      errors.title = this.translateFn("creatorMessages.errors.titleRequired");
    }
    if (!data.content.trim()) {
      errors.content = this.translateFn("creatorMessages.errors.contentRequired");
    }
    if (!opts?.skipSegmentsAndSchedule && data.segments.length === 0) {
      errors.segments = this.translateFn("creatorMessages.errors.segmentsRequired");
    }
    if (data.ctaEnabled) {
      if (!data.ctaText.trim()) {
        errors.ctaText = this.translateFn("creatorMessages.errors.ctaTextRequired");
      }
      if (!data.ctaUrl.trim()) {
        errors.ctaUrl = this.translateFn("creatorMessages.errors.ctaUrlRequired");
      } else if (!/^(https?:\/\/|www\.).+/.test(data.ctaUrl.trim())) {
        errors.ctaUrl = this.translateFn("creatorMessages.errors.ctaUrlInvalid");
      }
    }
    if (!opts?.skipSegmentsAndSchedule && data.scheduleEnabled) {
      if (!data.scheduledAt) {
        errors.scheduledAt = this.translateFn("creatorMessages.errors.dateRequired");
      }
      if (!data.scheduledTime) {
        errors.scheduledTime = this.translateFn("creatorMessages.errors.timeRequired");
      }
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  private normalizeUrl(url: string): string {
    const trimmed = url.trim();
    if (trimmed.startsWith("www.")) return `https://${trimmed}`;
    return trimmed;
  }

  private getScheduledAt(data: CreatorMessageFormData): string | null {
    return data.scheduleEnabled && data.scheduledAt && data.scheduledTime
      ? new Date(`${data.scheduledAt}T${data.scheduledTime}`).toISOString()
      : null;
  }

  async saveAsDraft(): Promise<boolean> {
    const data = this.state.formData.get();

    const errors: CreatorMessageFormErrors = {};
    if (!data.title.trim()) {
      errors.title = this.translateFn("creatorMessages.errors.titleRequired");
    }
    if (!data.content.trim()) {
      errors.content = this.translateFn("creatorMessages.errors.contentRequired");
    }
    if (data.segments.length === 0) {
      errors.segments = this.translateFn("creatorMessages.errors.segmentsRequired");
    }
    if (Object.keys(errors).length > 0) {
      this.state.validationErrors.set(errors);
      return false;
    }

    this.state.isSubmitting.set(true);

    try {
      const messageId = this.state.messageId.get();
      const scheduledAt = this.getScheduledAt(data);

      // rpcService.call() throws on failure and returns the unwrapped result directly
      if (messageId) {
        const result = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
          id: messageId,
          title: data.title,
          content: data.content,
          segments: data.segments,
          ctaEnabled: data.ctaEnabled,
          ctaText: data.ctaEnabled ? data.ctaText : null,
          ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : null,
          scheduledAt,
          imageFileId: data.imageFileId || null,
          status: "draft",
        });
        this.state.messageStatus.set(result.status);
      } else {
        const result = await this.rpcService.call<CreateCreatorMessageResult>("create-creator-message", {
          title: data.title,
          content: data.content,
          segments: data.segments,
          ctaEnabled: data.ctaEnabled,
          ctaText: data.ctaEnabled ? data.ctaText : undefined,
          ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : undefined,
          scheduledAt,
          imageFileId: data.imageFileId || null,
        });
        this.state.messageId.set(result.id);
        this.state.messageStatus.set(result.status);
      }
      return true;
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async saveAndSchedule(): Promise<boolean> {
    if (!this.validateForm()) return false;

    const data = this.state.formData.get();
    this.state.isSubmitting.set(true);

    try {
      const messageId = this.state.messageId.get();
      const scheduledAt = this.getScheduledAt(data);

      const payload = {
        title: data.title,
        content: data.content,
        segments: data.segments,
        ctaEnabled: data.ctaEnabled,
        ctaText: data.ctaEnabled ? data.ctaText : null,
        ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : null,
        scheduledAt,
        imageFileId: data.imageFileId || null,
      };

      let resolvedMessageId: string;

      if (messageId) {
        const result = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
          id: messageId,
          ...payload,
          status: data.scheduleEnabled ? "scheduled" : undefined,
        });
        this.state.messageStatus.set(result.status);
        resolvedMessageId = messageId;
      } else {
        const createResult = await this.rpcService.call<CreateCreatorMessageResult>("create-creator-message", {
          ...payload,
        });
        this.state.messageId.set(createResult.id);
        resolvedMessageId = createResult.id;

        if (data.scheduleEnabled) {
          const scheduleResult = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
            id: createResult.id,
            status: "scheduled",
          });
          this.state.messageStatus.set(scheduleResult.status);
        }
      }

      if (!data.scheduleEnabled) {
        await this.rpcService.call<SendCreatorMessageResult>("send-creator-message", {
          messageId: resolvedMessageId,
        });
        this.state.messageStatus.set("sent");
      }

      return true;
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async loadMessage(id: string): Promise<void> {
    if (this.pendingLoadId === id && this.pendingLoad) {
      return this.pendingLoad;
    }
    this.pendingLoadId = id;
    this.pendingLoad = this.doLoadMessage(id);
    try {
      await this.pendingLoad;
    } finally {
      this.pendingLoad = undefined;
      this.pendingLoadId = undefined;
    }
  }

  private async doLoadMessage(id: string): Promise<void> {
    this.state.isLoading.set(true);
    try {
      const msg = await this.rpcService.call<GetCreatorMessageByIdResult>("get-creator-message-by-id", { id });
      const hasSchedule = !!msg.scheduled_at;
      const scheduledDate = hasSchedule ? msg.scheduled_at!.substring(0, 10) : "";
      const scheduledTime = hasSchedule ? msg.scheduled_at!.substring(11, 16) : "";

      this.state.formData.set({
        title: msg.title,
        content: msg.content,
        segments: msg.segments,
        ctaEnabled: msg.cta_enabled,
        ctaText: msg.cta_text ?? "",
        ctaUrl: msg.cta_url ?? "",
        scheduledAt: scheduledDate,
        scheduledTime: scheduledTime,
        scheduleEnabled: hasSchedule,
        imageFileId: msg.image_file_id ?? "",
        imageUrl: msg.image_url ?? "",
      });
      this.state.messageId.set(msg.id);
      this.state.messageStatus.set(msg.status);

      // Test-send is a transient runtime helper — never carry it across drafts.
      this.state.testSendEnabled.set(false);
      this.state.testRecipientEmails.set("");
      this.clearTestRecipientError();
    } finally {
      this.state.isLoading.set(false);
    }
  }

  async loadSegments(): Promise<void> {
    if (this.state.segmentsLoading.get()) return;
    this.state.segmentsLoading.set(true);
    try {
      const result = await this.rpcService.call<GetMessageSegmentsResult>("get-message-segments", {});
      this.state.availableSegments.set(result.segments);
    } catch {
      this.state.availableSegments.set([]);
    } finally {
      this.state.segmentsLoading.set(false);
    }
  }

  toggleTestSend(enabled: boolean): void {
    this.state.testSendEnabled.set(enabled);
    if (enabled) {
      if (!this.state.testRecipientEmails.get()) {
        const userEmail = this.authState.currentUser.get()?.email ?? "";
        this.state.testRecipientEmails.set(userEmail);
      }
    } else {
      this.state.testRecipientEmails.set("");
      this.clearTestRecipientError();
    }
  }

  updateTestRecipientEmails(value: string): void {
    this.state.testRecipientEmails.set(value);
    this.clearTestRecipientError();
  }

  private clearTestRecipientError(): void {
    const errors = this.state.validationErrors.get();
    if (errors.testRecipientEmails) {
      const newErrors = { ...errors };
      delete newErrors.testRecipientEmails;
      this.state.validationErrors.set(newErrors);
    }
  }

  private parseTestRecipients(): { emails: string[]; error?: string } {
    const raw = this.state.testRecipientEmails.get();
    const seen = new Set<string>();
    const emails: string[] = [];
    for (const part of raw.split(",")) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(trimmed);
    }

    if (emails.length === 0) {
      return { emails: [], error: this.translateFn("creatorMessages.errors.testEmailsRequired") };
    }
    if (emails.length > TEST_SEND_MAX_RECIPIENTS) {
      return { emails, error: this.translateFn("creatorMessages.errors.testEmailsTooMany") };
    }
    if (emails.some((e) => !EMAIL_REGEX.test(e))) {
      return { emails, error: this.translateFn("creatorMessages.errors.testEmailsInvalid") };
    }
    return { emails };
  }

  async sendTestMessage(): Promise<boolean> {
    if (!this.validateForm({ skipSegmentsAndSchedule: true })) return false;

    const { emails, error } = this.parseTestRecipients();
    if (error) {
      this.state.validationErrors.set({ ...this.state.validationErrors.get(), testRecipientEmails: error });
      return false;
    }

    const data = this.state.formData.get();
    this.state.isSendingTest.set(true);
    try {
      const params: SendTestCreatorMessageParams = {
        title: data.title,
        content: data.content,
        ctaEnabled: data.ctaEnabled,
        ctaText: data.ctaEnabled ? data.ctaText : null,
        ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : null,
        imageFileId: data.imageFileId || null,
        testRecipientEmails: emails,
      };
      const result = await this.rpcService.call<SendTestCreatorMessageResult>("send-test-creator-message", params);
      const successMsg = this.translateFn("creatorMessages.testSendSuccess").replace(
        "{emails}",
        result.sentTo.join(", "),
      );
      NotificationService.success(successMsg);
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const rateLimitMatch = message.match(/^RATE_LIMITED:(\d+)$/);
      if (rateLimitMatch) {
        const minutes = Math.max(1, Math.ceil(Number(rateLimitMatch[1]) / 60));
        const errMsg = this.translateFn("creatorMessages.errors.testRateLimited")
          .replace("{limit}", String(TEST_SEND_HOURLY_LIMIT))
          .replace("{minutes}", String(minutes));
        this.state.validationErrors.set({ ...this.state.validationErrors.get(), testRecipientEmails: errMsg });
      } else {
        NotificationService.error(this.translateFn("creatorMessages.errors.testSendFailed"));
      }
      return false;
    } finally {
      this.state.isSendingTest.set(false);
    }
  }

  setImage(fileId: string, imageUrl: string): void {
    const current = this.state.formData.get();
    this.state.formData.set({ ...current, imageFileId: fileId, imageUrl });
  }

  clearImage(): void {
    const current = this.state.formData.get();
    this.state.formData.set({ ...current, imageFileId: "", imageUrl: "" });
  }

  reset(): void {
    this.state.reset();
  }
}
